import React, { useEffect, useState } from "react";
import { endpoints } from "../lib/api";
import { Overline, Section } from "../components/UI";
import { SEO } from "../components/SEO";
import { ExternalLink } from "lucide-react";

export function ResearchLibrary() {
  const [items, setItems] = useState([]);
  const [guidelines, setGuidelines] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    endpoints.research().then(setItems).catch(() => {});
    endpoints.guidelines().then(setGuidelines).catch(() => {});
  }, []);
  const filtered = filter === "all" ? items : items.filter(i => i.type === filter);
  return (
    <Section>
      <SEO/>
      <Overline>Research Library</Overline>
      <h1 className="font-serif text-4xl md:text-6xl text-navy mt-3 tracking-tight leading-none">The evidence, curated.</h1>
      <p className="text-lg text-stone2 mt-5 max-w-2xl">Landmark trials, guideline summaries, and evidence updates — organised so clinicians can find what matters, and patients can see what shapes recommendations.</p>

      <div className="mt-10 flex flex-wrap gap-2">
        {[
          { k: "all", l: "All" },
          { k: "landmark_trial", l: "Landmark Trials" },
          { k: "evidence_summary", l: "Evidence Summaries" },
          { k: "journal_update", l: "Journal Updates" },
        ].map(f => (
          <button key={f.k} onClick={()=>setFilter(f.k)} data-testid={`rf-${f.k}`}
            className={`text-xs mono uppercase tracking-widest px-4 py-2 rounded-full border transition-colors ${filter === f.k ? "bg-navy text-white border-navy" : "border-line text-stone2 hover:border-navy hover:text-navy"}`}>
            {f.l}
          </button>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {filtered.map(r => (
          <div key={r.slug} className="bg-white border border-line p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">{r.type.replace('_',' ')}</div>
            <h3 className="font-serif text-2xl text-navy mt-2 leading-tight">{r.title}</h3>
            <div className="mono text-xs text-stone2 mt-2">{r.journal} · {r.year}{r.doi && <> · doi:{r.doi}</>}</div>
            <p className="text-sm text-stone2 mt-3">{r.summary}</p>
            {r.takeaways?.length > 0 && (
              <ul className="mt-3 space-y-1.5">
                {r.takeaways.map((t,i) => <li key={i} className="text-sm text-navy flex gap-2"><span className="text-prestige mt-1">◆</span>{t}</li>)}
              </ul>
            )}
          </div>
        ))}
      </div>

      <div className="mt-20">
        <Overline>Guideline Summaries</Overline>
        <div className="h-px w-10 bg-prestige my-4"/>
        <div className="grid md:grid-cols-2 gap-5">
          {guidelines.map(g => (
            <a key={g.slug} href={g.url} target="_blank" rel="noopener" data-testid={`gl-${g.slug}`} className="block bg-white border border-line p-6 hover:shadow-md transition-shadow group">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mono text-[10px] uppercase tracking-widest text-prestige">{g.issuing_body} · {g.year}</div>
                  <div className="font-serif text-xl text-navy mt-1 group-hover:underline underline-offset-4">{g.title}</div>
                  <p className="text-sm text-stone2 mt-2">{g.summary}</p>
                </div>
                <ExternalLink size={16} strokeWidth={1.5} className="text-stone2 shrink-0 mt-1"/>
              </div>
            </a>
          ))}
        </div>
      </div>
    </Section>
  );
}
